/* eslint-disable import/no-cycle */
import { defineStore } from 'pinia';
import {
  IState,
  ISpecialities,
  IUserInfo,
  IIndastries,
  IProviders,
  IResponse,
  ILabelInput,
  ITagInput,
  ISearchLabelInput,
  IApplicationSettings,
  IUserSettings,
  ISubscriptionQuota,
  IUserNotificationsSettings,
  IUserReShareSettings,
  IBoundCorporate,
  ICountries,
  IProfession,
  IProfessionDetail,
  IProvince,
  IBadge,
} from '@/types/interfaces';

import GeneralService from '@/services/GeneralService';
import AuthService from '@/modules/auth/services/AuthService';
import RegistrationService from '@/modules/registration/services/RegistrationService';

import { useRegistrationStore } from '@/modules/registration/store/registrationStore';
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';
import { useProfileStore } from '@/modules/profile/store/profileStore';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useAlertsStore } from '@/modules/alerts/store/alertsStore';
import { useMessagesStore } from '@/modules/messages/store/messagesStore';
import { BADGES_TYPES, BOTTOM_POPUP_TYPES } from '@/types/enums';
import InputLabelsService from '@/services/InputLabelsService';
import { IUploadInfo } from '@/composables/UseUpload';
import UserService from '@/services/UserService';

export type RootState = {
  mobileToken: string;
  user: IUserInfo;
  userSettings: IUserSettings;
  boundCorporate: IBoundCorporate;
  applicationSettings: IApplicationSettings[];
  states: IState[];
  provinces: IProvince[];
  countries: ICountries[];
  specialities: ISpecialities[];
  indastries: IIndastries[];
  providers: IProviders[];
  profession: IProfession[];
  professionDetail: IProfessionDetail;
  notificationSettings: IUserNotificationsSettings;
  sharingSettings: IUserReShareSettings[];
  searchValue: string;
  badgesAlert: number;
  badgesMessage: number;
  badgesContact: number;
  badgesGroup: number;
  badgesInGroup: number;
  BottomPopUpType: BOTTOM_POPUP_TYPES | null
  // createCasePopUp: boolean
  createFolderPopUp: boolean

  inputPhotoTags: ITagInput[]
  inputPhotoViewTags: ITagInput[]
  inputPhotoLabels: ILabelInput[]
  inputCaseTags: ITagInput[]
  inputCaseLabels: ILabelInput[]
  inputFileTags: ITagInput[]
  inputFileLabels: ILabelInput[]
  suggestedInputLabels: ILabelInput[]
  recentInputLabels: ILabelInput[]
  searchInputLabels: ISearchLabelInput[]
  isMobile: boolean
  isAndroid: boolean
  isNative: boolean
  isFlutter: boolean
  isResponsive: boolean

  softQuotaWarning: boolean
  hardQuotaWarning: boolean

  subscriptionQuota: ISubscriptionQuota

  uploadInfos: IUploadInfo[]
  mediaUploading: boolean
}

function normalizeStates(states: IState[]): IState[] {
  return states.map((state) => ({ value: state.value, label: `${state.value}, ${state.label}` } as IState));
}

function normalizeProvinces(provinces: IProvince[]): IProvince[] {
  return provinces.map((province) => ({ value: province.value, label: `${province.value}, ${province.label}` } as IProvince));
}

export const useMainStore = defineStore({
  id: 'mainStore',

  state: () => ({
    mobileToken: '',
    user: {} as IUserInfo,
    subscriptionQuota: {
      filesQuota: {
        current: 0,
        exceeded: false,
        maxHard: 0,
        maxSoft: 0,
      },
    } as ISubscriptionQuota,
    userSettings: {} as IUserSettings,
    boundCorporate: {} as IBoundCorporate,
    applicationSettings: [],
    states: [] as IState[],
    provinces: [] as IProvince[],
    countries: [] as ICountries[],
    specialities: [] as ISpecialities[],
    indastries: [] as IIndastries[],
    providers: [] as IProviders[],
    profession: [] as IProfession[],
    professionDetail: {} as IProfessionDetail,
    notificationSettings: {
      push: [
        { type: 'NEW_MESSAGE', enabled: false },
        { type: 'CASE_SHARED', enabled: false },
        { type: 'PHOTO_SHARED', enabled: false },
        { type: 'FILES_FOLDERS_SHARED', enabled: false },
        { type: 'CONNECTIONS', enabled: false }],
      mail: [
        { type: 'NEW_MESSAGE', enabled: false },
        { type: 'CASE_SHARED', enabled: false },
        { type: 'PHOTO_SHARED', enabled: false },
        { type: 'FILES_FOLDERS_SHARED', enabled: false },
        { type: 'CONNECTIONS', enabled: false },
      ],
    },
    sharingSettings: [
      {
        type: 'CASE',
        allowedResharing: false,
        allowedSave: false,
        allowedDisplay: false,
      },
      {
        type: 'PHOTO',
        allowedResharing: false,
        allowedSave: false,
        allowedDisplay: false,
      },
      {
        type: 'FILE',
        allowedResharing: false,
        allowedSave: false,
        allowedDisplay: false,
      },
    ],
    searchValue: '',
    badgesAlert: 0,
    badgesMessage: 0,
    badgesContact: 0,
    badgesGroup: 0,
    badgesInGroup: 0,
    BottomPopUpType: null,
    // createCasePopUp: false,
    createFolderPopUp: false,

    inputPhotoTags: [],
    inputPhotoViewTags: [],
    inputPhotoLabels: [],
    inputCaseTags: [],
    inputCaseLabels: [],
    inputFileTags: [],
    inputFileLabels: [],
    suggestedInputLabels: [],
    recentInputLabels: [],
    searchInputLabels: [],

    moveItem: null,
    isMobile: true,
    isAndroid: false,
    isNative: false,
    isFlutter: false,
    isResponsive: false,

    softQuotaWarning: false,
    hardQuotaWarning: false,

    uploadInfos: [],
    mediaUploading: false, // Used to avoid re-order before images uploaded, value changed in create case, then we use it in Case view
  } as RootState),

  getters: {
    // counter: number,
    // success: number,
    // error: number,
    // canBeSuccess: number,
    // total: number,
    // totalBytesSize: number,
    // totalBytesUploaded: number,

    getUploadInfo: (state) => {
      const upload: IUploadInfo = {
        counter: 0,
        success: 0,
        error: 0,
        canBeSuccess: 0,
        total: 0,
        totalBytesSize: 0,
        totalBytesUploaded: 0,
      };

      state.uploadInfos.forEach((info) => {
        upload.canBeSuccess += info.canBeSuccess;
        upload.counter += info.counter;
        upload.error += info.error;
        upload.success += info.success;
        upload.total += info.total;
        upload.totalBytesSize += info.totalBytesSize;
        upload.totalBytesUploaded += info.totalBytesUploaded;
      });

      return upload;
    },
  },

  actions: {

    getSuggestedInputLabels(isFile = false, isPhoto = false): ILabelInput[] {
      // const tags = isFile ? this.inputFileTags : this.inputCaseTags;
      // eslint-disable-next-line no-nested-ternary
      const tags = isFile ? this.inputFileTags : isPhoto ? this.inputPhotoTags : this.inputCaseTags;
      const labels = this.suggestedInputLabels.filter(((lab) => !tags.find((searchLab) => searchLab.text.toLowerCase() === lab.name.toLowerCase())));
      return labels.length > 7 ? labels.slice(0, 7) : labels;
    },

    getRecentInputLabels(isFile = false, isPhoto = false): ISearchLabelInput[] {
      // eslint-disable-next-line no-nested-ternary
      const tags = isFile ? this.inputFileTags : isPhoto ? this.inputPhotoViewTags : this.inputCaseTags; // TODO refactor to inputPhotoViewTags except inputCaseTags GL
      const labels = this.recentInputLabels.filter(((lab) => !tags.find((searchLab) => searchLab.text.toLowerCase() === lab.name.toLowerCase())));
      return labels.length > 7 ? labels.slice(0, 7) : labels;
    },

    getSearchInputLabels(isFile = false, isPhoto = false): ISearchLabelInput[] {
      // const tags = isFile ? this.inputFileTags : this.inputCaseTags;
      // eslint-disable-next-line no-nested-ternary
      const tags = isFile ? this.inputFileTags : isPhoto ? this.inputPhotoTags : this.inputCaseTags;
      const labels = this.searchInputLabels.filter(((lab) => !tags.find((searchLab) => searchLab.text.toLowerCase() === lab.name.toLowerCase())));
      return labels.length > 7 ? labels.slice(0, 7) : labels;
    },

    resetUser(): void {
      this.user = {} as IUserInfo;
    },

    // openCreateCasePopUp() {
    //   this.createCasePopUp = true;
    // },

    // closeCreateCasePopUp() {
    //   this.createCasePopUp = false;
    // },

    openCreateFolderPopUp() {
      this.createFolderPopUp = true;
    },

    closeCreateFolderPopUp() {
      this.createFolderPopUp = false;
    },

    async fetchInputLabels(): Promise<ILabelInput[]> {
      return InputLabelsService.getInputLabels()
        .then((response) => {
          this.suggestedInputLabels = response.results.data;
          return this.suggestedInputLabels;
        });
    },

    async fetchNotificationSettings(): Promise<IUserNotificationsSettings> {
      return UserService.getNotificationSettings()
        .then((response) => {
          this.notificationSettings = response.results.data;
          return this.notificationSettings;
        });
    },

    async updateNotificationSettings(): Promise<IUserNotificationsSettings> {
      return UserService.setNotificationSettings(this.notificationSettings).then((response) => response.results.data);
    },

    async fetchSharingSettings(): Promise<IUserReShareSettings[]> {
      return UserService.getReSharingSettings().then((response) => {
        this.sharingSettings = response.results.data;
        return this.sharingSettings;
      });
    },

    async fetchUserTutorialInfo(): Promise<IResponse> {
      return UserService.getUserTutorialInfo().then((response) => {
        if (response.success) {
          return response.results;
        }
        throw response;
      });
    },

    async updateSharingSettings(): Promise<IUserReShareSettings[]> {
      return UserService.setReSharingSettings(this.sharingSettings).then((response) => response.results.data);
    },

    async fetchSubscriptionQuota() {
      return GeneralService.getSubscriptionQuota().then((response) => {
        this.subscriptionQuota = response.results.data;
        return this.subscriptionQuota;
      });
    },

    async fetchPersonalLabels(): Promise<ILabelInput[]> {
      return InputLabelsService.getPersonalLabels().then((response) => {
        if (response.success) {
          this.recentInputLabels = [...response.results.data];
        }
        return this.recentInputLabels;
      });
    },

    async createInputLabel(name: string): Promise<ILabelInput> {
      return InputLabelsService.createInputLabel(name).then((response) => {
        if (response.success) {
          return response.results.data;
        }
        throw response;
      });
    },

    async onSearchInputLabels(query: string): Promise<ISearchLabelInput[]> {
      return InputLabelsService.searchInputLabels(query).then((response) => {
        if (response.success) {
          this.searchInputLabels = response.results.data;
        }
        return this.searchInputLabels;
      });
    },

    lablesToTagsInput(labels: ILabelInput[], isFile = false, isPhoto = false): void {
      if (isFile) {
        this.inputFileTags = labels.map((label) => ({ text: label.name, id: label.id }));
      } else if (isPhoto) {
        this.inputPhotoTags = labels.map((label) => ({ text: label.name, id: label.id }));
      } else {
        this.inputCaseTags = labels.map((label) => ({ text: label.name, id: label.id }));
      }
    },

    // TODO - rewrite to ENUMS isFile, isPhoto -> LABELS_TYPE.FILE, LABELS_TYPE.PHOTO ...
    async tagsToLabelInput(isFile = false, isPhoto = false): Promise<ILabelInput[]> {
      const labels: ILabelInput[] = [];

      const createTags = async (tag: ITagInput) => {
        if (tag.id) {
          labels.push({ id: tag.id, name: tag.text });
          return tag;
        }
        let label = this.recentInputLabels.find((lab) => lab.name === tag.text) || this.suggestedInputLabels.find((lab) => lab.name === tag.text);
        if (!label) {
          label = await this.createInputLabel(tag.text);
        }
        labels.push(label);
        return { ...tag, id: label.id };
      };

      if (isFile) {
        this.inputFileTags = await Promise.all(this.inputFileTags.map(createTags));

        this.inputFileLabels = labels;
      } else if (isPhoto) {
        this.inputPhotoTags = await Promise.all(this.inputPhotoTags.map(createTags));
        this.inputPhotoLabels = labels;
      } else {
        this.inputCaseTags = await Promise.all(this.inputCaseTags.map(createTags));

        this.inputCaseLabels = labels;
      }

      return labels;
    },

    async fetchStates(): Promise<IState[]> {
      if (this.states?.length) {
        return new Promise((resolve) => resolve(this.states));
      }
      return GeneralService.getStates().then((response) => {
        if (response.success) {
          this.states = normalizeStates(response.results.states);
        }
        return this.states;
      });
    },

    async fetchProvinces(): Promise<IProvince[]> {
      if (this.provinces?.length) {
        return new Promise((resolve) => resolve(this.provinces));
      }
      return GeneralService.getProvinces().then((response) => {
        if (response.success) {
          this.provinces = normalizeProvinces(response.results.provinces);
        }
        return this.provinces;
      });
    },

    async fetchCountries(): Promise<ICountries[]> {
      if (this.countries?.length) {
        return new Promise((resolve) => resolve(this.countries));
      }
      return GeneralService.getCountries().then((response) => {
        if (response.success) {
          this.countries = response.results.data;
        }
        return this.countries;
      });
    },

    async fetchSpecialities(): Promise<ISpecialities[]> {
      if (this.specialities?.length) {
        return new Promise((resolve) => resolve(this.specialities));
      }
      return GeneralService.selectedSpeciality().then((response) => {
        if (response.success) {
          this.specialities = response.results.data;
        }
        return this.specialities;
      });
    },

    async fetchIndastries(): Promise<IIndastries[]> {
      if (this.indastries?.length) {
        return new Promise((resolve) => resolve(this.indastries));
      }
      return RegistrationService.getIndastriesData().then((response) => {
        if (response.success) {
          this.indastries = response.results.data;
        }
        return this.indastries;
      });
    },

    async fetchProvides(): Promise<IIndastries[]> {
      if (this.providers?.length) {
        return new Promise((resolve) => resolve(this.providers));
      }
      return RegistrationService.getProvidersData().then((response) => {
        if (response.success) {
          this.providers = response.results.data;
        }
        return this.providers;
      });
    },

    async fetchHealthcare(): Promise<IProfession[]> {
      if (this.profession?.length) {
        return new Promise((resolve) => resolve(this.profession));
      }
      return RegistrationService.getHealthcareData().then((response) => {
        if (response.success) {
          this.profession = response.results.data;
        }
        return this.profession;
      });
    },

    async fetchProfessionDetail(value: number): Promise<IProfessionDetail> {
      return RegistrationService.getProfessionDetailData(value).then((response) => {
        if (response.success) {
          this.professionDetail = response.results.data;
        }
        return this.professionDetail;
      });
    },

    async fetchUserBadges(): Promise<IBadge[]> {
      return UserService.fetchUserBadges().then((response) => {
        if (response.success) {
          response.results.data.forEach((badge) => {
            switch (badge.type) {
              case BADGES_TYPES.MESSAGES:
                this.badgesMessage = badge.amount;
                break;
              case BADGES_TYPES.ALERTS:
                this.badgesAlert = badge.amount;
                break;
              case BADGES_TYPES.CONTACT_REQUESTS:
                this.badgesContact = badge.amount;
                break;
              case BADGES_TYPES.GROUP_REQUESTS:
                this.badgesGroup = badge.amount;
                break;
              case BADGES_TYPES.IN_GROUP_REQUESTS:
                this.badgesInGroup = badge.amount;
                break;
              default:
                break;
            }
          });
        }
        return response.results.data;
      });
    },

    resetAllStores() {
      const registrationStore = useRegistrationStore();
      const spacesStore = useSpacesStore();
      const profileStore = useProfileStore();
      const driveStore = useDriveStore();
      const alertsStore = useAlertsStore();
      const messagesStore = useMessagesStore();

      this.$reset();
      registrationStore.$reset();
      spacesStore.$reset();
      profileStore.$reset();
      driveStore.$reset();
      alertsStore.$reset();
      messagesStore.$reset();
    },

    async logout(): Promise<IResponse<any>> {
      return AuthService.logout().then((response) => {
        if (response.success) {
          this.resetAllStores();
        }
        return response;
      });
    },
  },
});
